import React from 'react'
import styled, { keyframes } from 'styled-components'

import {
  ANIMATION_CYCLES_PER_WORD,
  ANIMATION_DURATION,
  PHRASE_FADE_MS,
  useCyclingLoadingPhrase,
} from 'features/ask-yogi/features/conversation/hooks/use-cycling-loading-phrase'

export interface ConversationLoadingViewProps {
  /** Seconds for one shimmer flutter. Defaults to ANIMATION_DURATION. */
  animationDuration?: number
  /** Flutters per phrase before cycling. Defaults to ANIMATION_CYCLES_PER_WORD. */
  animationCyclesPerWord?: number
  /** When false, the loader renders nothing and the cycle pauses. Defaults to true. */
  isActive?: boolean
}

// One keyframe iteration shifts the gradient by exactly one tile (one
// background-size width = 200%), so the loop point is visually identical to the
// start — the highlight sweeps once with no teleport/reset at the seam.
const shimmer = keyframes`
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: 0% 0;
  }
`

const FadeContainer = styled.div<{ $visible: boolean }>`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity ${PHRASE_FADE_MS}ms ease;
`

const ShimmerText = styled.span<{ $animationDuration: number }>`
  display: inline-block;
  font-size: 16px;
  font-weight: 500;
  background: linear-gradient(
    90deg,
    #8c8c8c 0%,
    #8c8c8c 35%,
    #28364f 50%,
    #8c8c8c 65%,
    #8c8c8c 100%
  );
  background-size: 200% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  animation: ${shimmer} ${({ $animationDuration }) => $animationDuration}s
    linear infinite;
`

/**
 * Loading indicator shown while Ask Yogi is generating a response. Cycles
 * through the placeholder phrases with an animated shimmer sweep, cross-fading
 * between them — no progress bar. Each phrase shimmers `animationCyclesPerWord`
 * times (one sweep per `animationDuration` seconds) before rotating to the next.
 */
export const ConversationLoadingView: React.FC<
  ConversationLoadingViewProps
> = ({
  animationDuration = ANIMATION_DURATION,
  animationCyclesPerWord = ANIMATION_CYCLES_PER_WORD,
  isActive = true,
}) => {
  const { phrase, visible } = useCyclingLoadingPhrase(
    isActive,
    animationDuration * animationCyclesPerWord * 1000,
  )

  if (!isActive) return null

  return (
    <FadeContainer $visible={visible}>
      <ShimmerText $animationDuration={animationDuration}>{phrase}</ShimmerText>
    </FadeContainer>
  )
}
